import Layout from "@/components/layout/Layout";
import PageHero from "@/components/shared/PageHero";
import ServerPricingTable from "@/components/shared/ServerPricingTable";
import FAQSection from "@/components/home/FAQSection";
import PartnersSection from "@/components/home/PartnersSection";
import SEOHead from "@/components/seo/SEOHead";
import SchemaMarkup from "@/components/seo/SchemaMarkup";
import { Server, Shield, Wrench, Headphones, HardDrive, Cpu } from "lucide-react";

const packages = [
  {
    name: "BDIX M-VPS 1",
    processor: "4 Core",
    ram: "8 GB",
    disk: "80 GB",
    bandwidth: "1 TB",
    originalPrice: "4,348",
    price: "2,174",
    period: "/month",
    orderLink: "https://my.webhostbuybd.com/store/bdix-managed-vps/m-vps-1",
  },
  {
    name: "BDIX M-VPS 2",
    processor: "8 Core",
    ram: "12 GB",
    disk: "120 GB",
    bandwidth: "2 TB",
    originalPrice: "6,506",
    price: "3,253",
    period: "/month",
    orderLink: "https://my.webhostbuybd.com/store/bdix-managed-vps/m-vps-2",
  },
  {
    name: "BDIX M-VPS 3",
    processor: "16 Core",
    ram: "24 GB",
    disk: "160 GB",
    bandwidth: "4 TB",
    originalPrice: "10,892",
    price: "5,446",
    period: "/month",
    orderLink: "https://my.webhostbuybd.com/store/bdix-managed-vps/m-vps-3",
  },
  {
    name: "BDIX M-VPS 4",
    processor: "24 Core",
    ram: "32 GB",
    disk: "220 GB",
    bandwidth: "Unmetered",
    originalPrice: "17,338",
    price: "8,669",
    period: "/month",
    orderLink: "https://my.webhostbuybd.com/store/bdix-managed-vps/m-vps-4",
  },
  {
    name: "BDIX M-VPS 5",
    processor: "32 Core",
    ram: "48 GB",
    disk: "460 GB",
    bandwidth: "Unmetered",
    originalPrice: "21,728",
    price: "10,864",
    period: "/month",
    orderLink: "https://my.webhostbuybd.com/store/bdix-managed-vps/m-vps-5",
  },
  {
    name: "BDIX M-VPS 6",
    processor: "56 Core",
    ram: "64 GB",
    disk: "850 GB",
    bandwidth: "Unmetered",
    originalPrice: "29,998",
    price: "14,999",
    period: "/month",
    orderLink: "https://my.webhostbuybd.com/store/bdix-managed-vps/m-vps-6",
  },
];

const features = [
  { icon: Wrench, title: "Fully Managed", description: "We handle everything" },
  { icon: Server, title: "Root Access", description: "Full server control" },
  { icon: HardDrive, title: "NVMe SSD", description: "Enterprise BDIX storage" },
  { icon: Cpu, title: "Dedicated CPU", description: "Guaranteed resources" },
  { icon: Shield, title: "Security", description: "Managed firewall" },
  { icon: Headphones, title: "24/7 Support", description: "Expert Bangladesh team" },
];

const managedFaqs = [
  {
    question: "What is BDIX hosting and why is it important?",
    answer: "BDIX (Bangladesh Internet Exchange) hosting provides ultra-fast connectivity within Bangladesh by routing traffic through local exchange points. This results in significantly lower latency and faster page load times for visitors within Bangladesh, making it ideal for local businesses and websites targeting Bangladeshi audiences.",
  },
  {
    question: "Do you offer free website migration?",
    answer: "Yes! We offer completely free website migration for all our hosting plans. Our expert team will migrate your website, databases, emails, and all files from your current host to our servers with zero downtime.",
  },
  {
    question: "What control panel do you use?",
    answer: "We use cPanel, the industry's most popular and user-friendly control panel. It provides an intuitive interface for managing your website, emails, databases, files, and more. We also offer one-click installers for WordPress, Joomla, and 400+ other applications.",
  },
  {
    question: "What is your uptime guarantee?",
    answer: "We guarantee 99.99% uptime for all our hosting services, backed by our Service Level Agreement (SLA). Our state-of-the-art datacenter infrastructure, redundant power systems, and 24/7 monitoring ensure your website stays online.",
  },
  {
    question: "Do you provide daily backups?",
    answer: "Yes, we perform automated daily backups of all hosting accounts. Backups are stored on separate servers for added security. You can also create manual backups anytime through cPanel or request restoration from our support team.",
  },
  {
    question: "How fast is your customer support?",
    answer: "Our customer support team is available 24/7 via live chat, phone, and ticket system. Average response time for live chat is under 2 minutes, and most tickets are resolved within 1 hour. Our support team consists of certified hosting experts.",
  },
];

const ManagedVPS = () => {
  return (
    <Layout>
       <SEOHead
         title="Best VPS Hosting Bangladesh | Fastest BDIX Managed VPS - Up to 60% OFF"
         description="Best VPS hosting in Bangladesh - fastest BDIX VPS with 24/7 expert management. NVMe SSD, daily backups &amp; security patches included. Best VPS hosting provider in Bangladesh from ৳2,174/month. BDIX VPS hosting with dedicated resources."
         canonical="/bdix-managed-vps"
         type="product"
         keywords="fastest BDIX VPS, best VPS hosting in Bangladesh, best VPS hosting provider in Bangladesh, BDIX VPS hosting, cheap VPS in Bangladesh, NVMe SSD hosting, reliable web hosting Bangladesh, cheapest BDIX VPS, bdix vps, bdix vps hosting, bdix vps server, bdix vps price in bangladesh, best bdix vps, vps server bangladesh, vps hosting in bangladesh, bdix cloud vps, turbo vps"
       />
      <SchemaMarkup
        type="product"
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "BDIX Managed VPS", url: "/bdix-managed-vps" },
        ]}
        faqs={managedFaqs}
        products={packages.map((p) => ({
          name: `${p.name} - Best VPS Bangladesh`,
          description: `Fastest BDIX VPS - ${p.processor}, ${p.ram} RAM, ${p.disk} NVMe SSD with 24/7 support`,
          price: p.price.replace(",", ""),
        }))}
        pageName="Best VPS Hosting Bangladesh - Fastest BDIX Managed VPS"
        pageDescription="Best VPS hosting in Bangladesh, fastest BDIX VPS with 24/7 support. Best VPS hosting provider in Bangladesh from ৳2,174/month. BDIX VPS hosting."
      />
      <PageHero
        badge="Best BDIX VPS"
        title="Best VPS Hosting in Bangladesh"
        subtitle="Fastest BDIX VPS with fully managed 24/7 expert support. NVMe SSD, security updates, and daily backups included."
      />

      {/* Features Strip */}
      <section className="py-12 bg-muted/30 border-b border-border" aria-label="BDIX Managed VPS Features">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {features.map((feature) => (
              <div
                key={feature.title}
                className="flex items-center gap-3"
              >
                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0" aria-hidden="true">
                  <feature.icon className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <div className="font-medium text-foreground text-sm">{feature.title}</div>
                  <div className="text-xs text-muted-foreground">{feature.description}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ServerPricingTable
        packages={packages}
        title="Choose Your BDIX Managed VPS Plan"
        subtitle="All BDIX managed VPS plans include 24/7 monitoring, security patches, and expert support. Affordable hosting price in Bangladesh."
      />

      <FAQSection />
      <PartnersSection />
    </Layout>
  );
};

export default ManagedVPS;
